import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await notificationService?.getAll();
      setNotifications(data);
    } catch (err) {
      console.error('Failed to load notifications:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();

    // Real-time subscription
    channelRef.current = notificationService?.subscribeToNew(user?.id, (newNotif) => {
      setNotifications(prev => [newNotif, ...prev]);
    });

    return () => {
      notificationService?.unsubscribe(channelRef?.current);
    };
  }, [user, load]);

  const markRead = useCallback(async (id) => {
    try {
      await notificationService?.markRead(id);
      setNotifications(prev =>
        prev?.map(n => n?.id === id ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      console.error('markRead failed:', err?.message);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationService?.markAllRead();
      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('markAllRead failed:', err?.message);
    }
  }, []);

  const remove = useCallback(async (id) => {
    try {
      await notificationService?.delete(id);
      setNotifications(prev => prev?.filter(n => n?.id !== id));
    } catch (err) {
      console.error('delete notification failed:', err?.message);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, loading, markRead, markAllRead, remove, refresh: load }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
